import React from 'react';
import { styled } from '@linaria/react';

import { sample, random } from '@/utils';

import { DemoSliderControl } from '@/components/Demo';

import FlexDemo from './FlexDemo';
import Indicator from './Indicator';

const POSSIBLE_NAMES = [
  'Maria',
  'Nushi',
  'Mohammed',
  'Jose',
  'Wei',
  'Ahmed',
  'John',
  'David',
  'Anna',
  'Mary',
  'Jean',
  'Elena',
  'Min',
  'Fatima',
  'Aleksandr',
  'Lin',
  'Olga',
  'Rosa',
  'Jorge',
  'Elizabeth',
  'Patricia',
  'Hassan',
  'Anita',
];
const PROVIDERS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'aol.com',
  'yahoo.co.uk',
  'live.com',
  'web.de',
  'libero.it',
  'comcast.net',
];

function ArgyleDemo({ showInteractionFlag }) {
  const id = React.useId();

  const [name, setName] = React.useState(null);
  const [email, setEmail] = React.useState(null);

  const [hasInteracted, setHasInteracted] = React.useState(false);

  const [scale, setScale] = React.useState(1);
  const freshScale = React.useRef();
  freshScale.current = scale;

  React.useEffect(() => {
    const name = sample(POSSIBLE_NAMES);
    const email = `${name}${random(1000, 9999)}@${sample(PROVIDERS)}`;

    setName(name);
    setEmail(email);
  }, []);

  React.useEffect(() => {
    function calculateScale() {
      const windowWidth = window.innerWidth;
      const requiredSpace = 600 + 16 * 2 + 32;

      if (windowWidth < requiredSpace) {
        setScale(windowWidth / requiredSpace);
      } else if (freshScale.current !== 1) {
        setScale(1);
      }
    }

    calculateScale();

    window.addEventListener('resize', calculateScale);

    return () => {
      window.removeEventListener('resize', calculateScale);
    };
  }, []);

  return (
    <FlexDemo
      centered
      controlPosition="top"
      customGrid={{
        gridTemplateColumns: 'max(300px, 100%)',
      }}
      initialValues={{
        containerWidth: 600,
      }}
      controls={[
        <SliderWrapper key="containerWidth">
          <DemoSliderControl
            id="containerWidth"
            label="Container width"
            min={250}
            max={600}
            step={10}
            handleFocusColor="white"
            wrapperStyle={{
              width: 400,
              maxWidth: '80vw',
            }}
            onChange={() => {
              if (!hasInteracted) {
                setHasInteracted(true);
              }
            }}
          />
          <IndicatorWrapper
            style={{
              opacity: showInteractionFlag && !hasInteracted ? 1 : 0,
            }}
          >
            <Indicator />
          </IndicatorWrapper>
        </SliderWrapper>,
      ]}
    >
      {({ containerWidth }) => {
        return (
          <Form
            style={{
              width: containerWidth,
              transform: `scale(${scale})`,
            }}
            onSubmit={(event) => {
              alert('Not a real form, I’m afraid 🙁');
              event.preventDefault();
            }}
          >
            <NameLabel htmlFor={`name-${id}`}>
              Name:
              <Input
                id={`name-${id}`}
                type="text"
                placeholder={name}
              />
            </NameLabel>
            <EmailLabel htmlFor={`email-${id}`}>
              Email:
              <Input
                id={`email-${id}`}
                type="email"
                placeholder={email}
              />
            </EmailLabel>
            <Button>Submit</Button>
          </Form>
        );
      }}
    </FlexDemo>
  );
}

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IndicatorWrapper = styled.div`
  position: absolute;
  bottom: 1px;
  right: -16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transform: translate(100%, 0%);
  transition: opacity 400ms;
  user-select: none;
  pointer-events: none;

  /* Doesn't fit below a certain screen size */
  @media (max-width: 700px) {
    display: none;
  }
`;

const Form = styled.form`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  padding: 8px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const Label = styled.label`
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
`;

const NameLabel = styled(Label)`
  flex: 1 1 160px;
`;
const EmailLabel = styled(Label)`
  flex: 3 1 200px;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 2px solid var(--color-gray-700);
  color: var(--color-text);
  height: 2.5rem;
  border-radius: 2px;
  outline-color: hsl(50deg 100% 60%);
  outline-offset: 4px;
  font-weight: normal;
`;
const Button = styled.button`
  flex: 1 1 80px;
  height: 2.5rem;
  background: hsl(345deg 0% 90%);
  border: none;
  color: var(--color-background);
  border-radius: 4px;
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  outline-color: hsl(50deg 100% 60%);
  outline-offset: 4px;
  text-align: center;
`;

export default ArgyleDemo;
